import { Add as AddIcon, Visibility as VisibilityIcon } from "@mui/icons-material";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { ptBR } from "date-fns/locale";
import { useCallback, useEffect, useRef, useState } from "react";
import NovoRequerimentoIsencaoModal from "../../components/NovoRequerimentoIsencaoModal";
import VisualizarIsencaoModal from "../../components/VisualizarIsencaoModal";
import empreendimentoService from "../../services/empreendimento";
import isencaoLicenciamentoConsultaService from "../../services/isencaoLicenciamentoConsulta";

function IsencaoLicenciamentoPage() {
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPaginas, setTotalPaginas] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [orderBy, setOrderBy] = useState("dataSolicitacao");
  const [orderDirection, setOrderDirection] = useState("desc");
  const [loadingLista, setLoadingLista] = useState(false);

  // Filtros
  const [empreendimentos, setEmpreendimentos] = useState([]);
  const [filtroEmpreendimento, setFiltroEmpreendimento] = useState(null);
  const [dataInicio, setDataInicio] = useState(null);
  const [dataFim, setDataFim] = useState(null);
  const [erroData, setErroData] = useState("");

  const debounceRef = useRef(null);

  const [modalRequerimentoAberto, setModalRequerimentoAberto] = useState(false);
  const [isencaoVisualizar, setIsencaoVisualizar] = useState(null);
  const [loadingVisualizarIsencao, setLoadingVisualizarIsencao] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const handleVisualizarIsencao = async (isencaoId) => {
    setLoadingVisualizarIsencao(true);
    try {
      const isencaoCompleta = await isencaoLicenciamentoConsultaService.obterPorId(isencaoId);
      setIsencaoVisualizar(isencaoCompleta);
    } catch {
      setSnackbar({ open: true, message: "Erro ao carregar detalhes da isenção", severity: "error" });
    } finally {
      setLoadingVisualizarIsencao(false);
    }
  };

  useEffect(() => {
    empreendimentoService
      .listarPaginado({}, 1, 100)
      .then((result) => setEmpreendimentos(result.items || []))
      .catch(() => setEmpreendimentos([]));
  }, []);

  const carregarLista = useCallback(async (pg, ps, empId, dtInicio, dtFim, ob, od) => {
    setLoadingLista(true);
    try {
      const dtInicioParam = dtInicio ? dtInicio.toISOString() : null;
      const dtFimParam = dtFim ? dtFim.toISOString() : null;
      const result = await isencaoLicenciamentoConsultaService.listarMinhas(
        pg, ps, empId, dtInicioParam, dtFimParam, ob, od
      );
      setRows(result?.data?.data ?? []);
      setTotalCount(result?.data?.totalCount ?? 0);
      setTotalPaginas(result?.data?.totalPages ?? 0);
    } catch {
      setRows([]);
      setTotalCount(0);
      setTotalPaginas(0);
    } finally {
      setLoadingLista(false);
    }
  }, []);

  useEffect(() => {
    if (erroData) return;
    carregarLista(pageNumber, pageSize, filtroEmpreendimento?.id ?? null, dataInicio, dataFim, orderBy, orderDirection);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNumber, pageSize, orderBy, orderDirection]);

  const aplicarFiltros = (empId, dtInicio, dtFim) => {
    if (dtInicio && dtFim && dtInicio > dtFim) {
      setErroData("A data de início não pode ser posterior à data de fim.");
      return;
    }
    setErroData("");
    setPageNumber(1);
    carregarLista(1, pageSize, empId, dtInicio, dtFim, orderBy, orderDirection);
  };

  const handleEmpreendimentoChange = (_, val) => {
    setFiltroEmpreendimento(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      aplicarFiltros(val?.id ?? null, dataInicio, dataFim);
    }, 400);
  };

  const handleDataInicioChange = (val) => {
    setDataInicio(val);
    aplicarFiltros(filtroEmpreendimento?.id ?? null, val, dataFim);
  };

  const handleDataFimChange = (val) => {
    setDataFim(val);
    aplicarFiltros(filtroEmpreendimento?.id ?? null, dataInicio, val);
  };

  const handleSort = (coluna) => {
    const isAsc = orderBy === coluna && orderDirection === "asc";
    const novaDir = isAsc ? "desc" : "asc";
    setOrderBy(coluna);
    setOrderDirection(novaDir);
    setPageNumber(1);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
      <Container sx={{ px: { xs: 2, sm: 3 } }}>
        <Box sx={{ mt: { xs: 2, sm: 4 }, mb: { xs: 2, sm: 4 } }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", sm: "center" }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Typography
              variant="h4"
              component="h1"
              sx={{ fontSize: { xs: "1.2rem", sm: "1.5rem", md: "2.125rem" } }}
            >
              Isenção de Licenciamento
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setModalRequerimentoAberto(true)}
              sx={{ width: { xs: "100%", sm: "auto" } }}
            >
              Novo Requerimento
            </Button>
          </Stack>

          <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Autocomplete
                  options={empreendimentos}
                  getOptionLabel={(op) => op.nome || op.razaoSocial || ""}
                  value={filtroEmpreendimento}
                  onChange={handleEmpreendimentoChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      label="Filtrar por Empreendimento"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <DatePicker
                  label="Data Início"
                  value={dataInicio}
                  onChange={handleDataInicioChange}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!erroData,
                      helperText: erroData,
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <DatePicker
                  label="Data Fim"
                  value={dataFim}
                  onChange={handleDataFimChange}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
            </Grid>
          </Paper>

          <TableContainer component={Paper} sx={{ overflowX: "auto" }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell sortDirection={orderBy === "nomeEmpreendimento" ? orderDirection : false}>
                    <TableSortLabel
                      active={orderBy === "nomeEmpreendimento"}
                      direction={orderBy === "nomeEmpreendimento" ? orderDirection : "asc"}
                      onClick={() => handleSort("nomeEmpreendimento")}
                    >
                      Empreendimento
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={orderBy === "dataSolicitacao" ? orderDirection : false}>
                    <TableSortLabel
                      active={orderBy === "dataSolicitacao"}
                      direction={orderBy === "dataSolicitacao" ? orderDirection : "asc"}
                      onClick={() => handleSort("dataSolicitacao")}
                    >
                      Data de Solicitação
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">Opções</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingLista ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      Nenhum registro encontrado
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell sx={{ fontFamily: "monospace", fontSize: "0.85rem" }}>
                        {row.id ? `${row.id.substring(0, 8)}...` : "—"}
                      </TableCell>
                      <TableCell>{row.empreendimentoNome || "—"}</TableCell>
                      <TableCell>
                        {row.createAt
                          ? new Date(row.createAt).toLocaleString("pt-BR")
                          : "—"}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          onClick={() => handleVisualizarIsencao(row.id)}
                          title="Visualizar"
                          aria-label="Visualizar isenção"
                          disabled={loadingVisualizarIsencao}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalCount}
            page={Math.max(0, pageNumber - 1)}
            onPageChange={(_, newPage) => setPageNumber(newPage + 1)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => {
              setPageSize(parseInt(e.target.value, 10));
              setPageNumber(1);
            }}
            rowsPerPageOptions={[5, 10, 20, 50]}
            labelRowsPerPage="Itens por página"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} de ${count}${totalPaginas > 0 ? ` | Página ${pageNumber} de ${totalPaginas}` : ""}`
            }
          />

          <NovoRequerimentoIsencaoModal
            open={modalRequerimentoAberto}
            onClose={() => setModalRequerimentoAberto(false)}
            onSuccess={(_isencaoId) => {
              setModalRequerimentoAberto(false);
              setSnackbar({ open: true, message: "Requerimento enviado com sucesso!", severity: "success" });
              carregarLista(pageNumber, pageSize, filtroEmpreendimento?.id ?? null, dataInicio, dataFim, orderBy, orderDirection);
            }}
          />

          <VisualizarIsencaoModal
            open={!!isencaoVisualizar}
            isencao={isencaoVisualizar}
            onClose={() => setIsencaoVisualizar(null)}
          />

          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          >
            <Alert
              onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
              severity={snackbar.severity}
              sx={{ width: "100%" }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Box>
      </Container>
    </LocalizationProvider>
  );
}

export default IsencaoLicenciamentoPage;

